<template>
  <v-app id="truple">
    <v-app-bar color="primary" app v-if="!$store.state.inApp || $store.state.showAppBar">
      <v-expand-transition v-if="isLoggedIn()">
        <v-app-bar-nav-icon @click.stop="drawer = (!drawer && isLoggedIn())"></v-app-bar-nav-icon>
      </v-expand-transition>
      <v-toolbar-title style="flex: 1 1 auto">
        <div class="d-flex align-center">
          <img src="/static/img/logo.3.white.svg" style="width: 32px; height: 32px;" />
          <span class="app-title ml-2">{{ appName }}</span>
        </div>
      </v-toolbar-title>
      <template v-if="showNewSupport">
        <div v-if="isLoggedIn()" style="position: relative; display: inline-flex; align-items: center;">
          <transition name="chat-bubble-anim">
          <div v-if="showChatTooltip" class="chat-bubble">
            {{ hasWaitingTicket ? 'New message' : 'Need help?' }}
            <div class="chat-bubble-tick"></div>
          </div>
          </transition>
          <v-btn icon variant="text" @click="openSupport" class="chat-pulse">
            <v-badge :model-value="hasWaitingTicket" color="error" dot>
              <v-icon>chat</v-icon>
            </v-badge>
          </v-btn>
        </div>
      </template>
    </v-app-bar>

    <!-- Quick support dialog -->
    <v-dialog v-model="showSupportDialog" max-width="600" persistent>
      <v-card>
        <v-card-title>New Support Request</v-card-title>
        <v-card-text>
          <p class="text-body-2 text-grey mb-4">
              Ask a question or describe your issue. Please provide as much detail as possible.
          </p>
          <v-alert
            v-if="showSupportProfanityWarning"
            variant="tonal"
            color="error"
            density="compact"
            class="mb-3"
          >
            {{ supportProfanityWarning }}
          </v-alert>
          <v-textarea
            v-model="supportMessage"
            label="How can we help?"
            rows="4"
            auto-grow
            variant="outlined"
          />
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn @click="cancelSupport" variant="text">Cancel</v-btn>
          <v-btn
            color="primary"
            :disabled="!supportMessage.trim()"
            @click="submitSupportMessage"
          >
            Submit
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Alpha / AI notice dialog -->
    <v-dialog v-model="showSupportAlphaWarning" max-width="550" persistent>
      <v-card>
        <v-card-title>A few things to know</v-card-title>
        <v-card-text>
          <v-alert variant="outlined" color="warning" density="compact" class="mb-3">
            <strong>Early Access</strong> — This feature is new and still being improved.
          </v-alert>

          <p class="text-body-2 mb-3">
            You'll be chatting with <strong>Alfred</strong>, our AI support assistant. He's pretty good, but here are a few things to keep in mind:
          </p>

          <ul class="text-body-2 ml-2" style="list-style: disc">
            <li class="mb-2">
              Alfred is <strong>constantly learning and improving</strong>. If something seems wrong, please <strong>request an engineer's help</strong> and we can clarify and, if appropriate, correct Alfred's knowledge on the topic.
            </li>
            <li class="mb-2">
              Please <strong>avoid sharing sensitive info</strong> like passwords or financial details in the chat. Messages are processed by third-party AI services per our
              <a :href="`https://${supportBaseDomain}/privacy.html`" target="_blank">Privacy Policy</a>.
            </li>
            <li class="mb-2">
              <strong>Support tickets are account wide</strong>, other devices on your account may be able to view and interact with your ticket.
            </li>
          </ul>
        </v-card-text>
        <v-card-actions>
          <v-btn @click="showSupportAlphaWarning = false" variant="text">Cancel</v-btn>
          <v-spacer />
          <v-btn color="primary" @click="submitSupport">
            Got it, let's go
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <v-navigation-drawer
            v-if="!$store.state.inApp || $store.state.showAppBar"
            v-model="drawer"
            fixed
            clipped
            temporary
            app
    >
      <v-list density="compact">
        <v-list-item @click="$router.push('login')" v-if="!isLoggedIn()">
          <template v-slot:prepend>
            <v-icon>account_circle</v-icon>
          </template>

            <v-list-item-title>Sign Up</v-list-item-title>

        </v-list-item>
        <v-list-item @click="$router.push('login')" v-if="!isLoggedIn()">
          <template v-slot:prepend>
            <v-icon>transit_enterexit</v-icon>
          </template>

            <v-list-item-title>Login</v-list-item-title>

        </v-list-item>
        <v-list-item @click="$router.push(homeRoute)"  v-if="isLoggedIn()" :disabled="$route.path.toLowerCase().indexOf('home') !== -1 || $route.path.toLowerCase().indexOf('userdevice') !== -1">
          <template v-slot:prepend>
            <v-icon>home</v-icon>
          </template>

            <v-list-item-title>Home</v-list-item-title>

        </v-list-item>
        <v-list-item @click="$router.push('/filter')"  v-if="isLoggedIn()" :disabled="$route.path.toLowerCase().indexOf('filter') !== -1">
          <template v-slot:prepend>
            <v-icon>security</v-icon>
          </template>

            <v-list-item-title>Web Filter</v-list-item-title>

        </v-list-item>
        <v-list-item @click="$router.push('/devices')"  v-if="isLoggedIn() && $store.getters.isOwner" :disabled="$route.path.toLowerCase().indexOf('devices') !== -1">
          <template v-slot:prepend>
            <v-icon>smartphone</v-icon>
          </template>

            <v-list-item-title>Devices</v-list-item-title>

        </v-list-item>
        <v-list-item @click="$router.push('/emails')"  v-if="isLoggedIn() && $store.getters.isOwner" :disabled="$route.path.toLowerCase().indexOf('emails') !== -1">
          <template v-slot:prepend>
            <v-icon>email</v-icon>
          </template>

            <v-list-item-title>Alerting & Reports</v-list-item-title>

        </v-list-item>
        <v-list-item @click="$router.push('/account')" v-if="isLoggedIn() && $store.getters.isOwner" :disabled="$route.path.toLowerCase().indexOf('account') !== -1">
          <template v-slot:prepend>
            <v-icon>account_box</v-icon>
          </template>

            <v-list-item-title>Account</v-list-item-title>

        </v-list-item>

        <v-list-item @click="$router.push('/setup')" v-if="isLoggedIn() && $store.getters.isOwner" :disabled="$route.path.toLowerCase().indexOf('setup') !== -1">
          <template v-slot:prepend>
            <v-icon>done_all</v-icon>
          </template>

            <v-list-item-title>Setup Guide</v-list-item-title>

        </v-list-item>


        <template v-if="showNewSupport">
          <v-list-item @click="$router.push('/support')" v-if="isLoggedIn()" :disabled="$route.path.toLowerCase().indexOf('support') !== -1">
            <template v-slot:prepend>
              <v-icon>chat</v-icon>
            </template>
              <v-list-item-title>Support</v-list-item-title>
          </v-list-item>
        </template>
        <v-list-item v-else-if="isLoggedIn()" :href="`https://${supportDomain}`">
          <template v-slot:prepend>
            <v-icon>chat</v-icon>
          </template>
          <v-list-item-title>Support</v-list-item-title>
        </v-list-item>

        <v-list-item v-if="product !== 'screenkin' && product !== 'bestaccountability'" href="https://stories.truple.io" target="_blank">
          <template v-slot:prepend>
            <v-icon>comment</v-icon>
          </template>

            <v-list-item-title>Community</v-list-item-title>

        </v-list-item>

        <v-list-item @click="$router.push('/rating')"  v-if="isLoggedIn() && product !== 'screenkin' && $store.getters.isOwner" :disabled="$route.path.toLowerCase().indexOf('rating') !== -1">
          <template v-slot:prepend>
            <v-icon>construction</v-icon>
          </template>

            <v-list-item-title>Analyzer</v-list-item-title>

        </v-list-item>
<!--        <v-list-item @click="$router.push('/discounts')" v-if="isLoggedIn()" :disabled="$route.path.toLowerCase().indexOf('discounts') !== -1">-->
<!--          <v-list-item-action>-->
<!--            <v-icon>monetization_on</v-icon>-->
<!--          </v-list-item-action>-->
<!--          <v-list-item-content>-->
<!--            <v-list-item-title>Discounts</v-list-item-title>-->
<!--          </v-list-item-content>-->
<!--        </v-list-item>-->
        <v-list-item @click="openBatteryOptimization()" v-if="isLoggedIn() && $store.state.inApp">
          <template v-slot:prepend>
            <v-icon>battery_alert</v-icon>
          </template>
            <v-list-item-title>Unstable Monitoring Fixes</v-list-item-title>
        </v-list-item>

        <v-list-item @click="openAbout()" v-if="isLoggedIn() && $store.state.inApp">
          <template v-slot:prepend>
            <v-icon>info</v-icon>
          </template>
            <v-list-item-title>About</v-list-item-title>
        </v-list-item>

        <v-list-item @click="logoutAwait('navigation-logout', true)" v-if="(isLoggedIn() || loggingOut) && !$store.state.inApp" :disabled="loggingOut">
          <template v-slot:prepend>
            <v-icon v-if="!loggingOut">exit_to_app</v-icon>
            <v-progress-circular indeterminate size="24" v-else class="mr-2"></v-progress-circular>
          </template>

            <v-list-item-title>Logout</v-list-item-title>

        </v-list-item>

        <v-list-item @click="$router.push('/debug')" v-if="isLoggedIn() && $store.state.account && $store.state.account.account.debug" :disabled="$route.path.toLowerCase().indexOf('debug') !== -1">
          <template v-slot:prepend>
            <v-icon>bug_report</v-icon>
          </template>

            <v-list-item-title>Debug</v-list-item-title>

        </v-list-item>
      </v-list>
    </v-navigation-drawer>
    <v-main class="mb-12">
      <v-alert v-if="clockSkewDetected"
               type="error"
               variant="tonal"
               density="compact"
               closable
               class="ma-3"
               @click:close="clockSkewDetected = false">
        Your device's clock is off by {{ clockSkewMinutes }} minutes. Please set your system clock to update automatically, then refresh the page. An accurate system clock is required to protect against replay attacks.
      </v-alert>
      <v-alert v-if="e2eeConnectionIssue"
               type="warning"
               variant="tonal"
               density="compact"
               closable
               class="ma-3"
               @click:close="e2eeConnectionIssue = false">
        Your device is having trouble connecting to our end-to-end encryption server. Some encrypted screenshots and events may not load until the connection is restored. If this keeps happening, please <a @click="openE2EESupportTicket" style="cursor: pointer; text-decoration: underline;">open a support ticket</a>.
      </v-alert>
      <router-view></router-view>
    </v-main>

    <v-snackbar
            v-if="displayUnsupportedBrowser && !$store.state.inApp"
            app
            location="top"
            :timeout="10000"
            v-model="displayUnsupportedBrowser">
      <v-icon color="accent" class="pr-3">new_releases</v-icon>
      <span>For the best experience, please use <a href="https://www.google.com/chrome">Chrome</a>, <a href="http://getfirefox.com">Firefox</a> or <a href="https://www.apple.com/safari/">Safari</a>.</span>
      <v-btn icon size="small" color="primary" class="pr-0 pl-3 pt-0 pb-0 ma-0" @click="displayUnsupportedBrowser = false">
        <v-icon>close</v-icon>
      </v-btn>
    </v-snackbar>

    <v-snackbar
      v-else-if="displayPastDue"
      app
      location="top"
      :timeout="10000"
      v-model="displayPastDue">
      <v-icon color="accent" class="pr-3">new_releases</v-icon>
      <span>Your account is past due. Please <a @click="fixPaymentInfo" class="text-decoration-underline" :style="{ color: $vuetify.theme.current.colors.accent + ' !important' }" style="cursor: pointer;">fix your card info</a>.</span>
      <v-btn icon variant="text" size="small" color="primary" class="pr-0 pl-3 pt-0 pb-0 ma-0" @click="displayPastDue = false">
        <v-icon>close</v-icon>
      </v-btn>
    </v-snackbar>

    <v-snackbar v-else-if="shouldAskForFeedback && !showFeedbackSurvey"
                app
                location="top end"
                max-width="80vw"
                :timeout="60000"
                v-model="shouldAskForFeedback"
    >
      <div class="d-flex align-center">
        <v-icon color="accent" class="mr-3">poll</v-icon>
        <span class="flex-grow-1">Let us know how we're doing!</span>
        <v-btn icon variant="text" size="small" color="white" class="ml-2" @click="overrideFeedbackRequest = true"><v-icon>close</v-icon></v-btn>
      </div>
      <div class="d-flex justify-end">
        <v-btn variant="text" color="accent" @click="openFeedbackSurvey">
          Take Survey
        </v-btn>
      </div>
    </v-snackbar>
    <survey-feedback v-model="showFeedbackSurvey" @on-close="reloadAccount" />

    <v-footer app absolute>
      <v-spacer></v-spacer>
      <div>
        <span class="text-caption text-medium-emphasis"><a :href="`https://${domain}/privacy.html`" target="_blank" class="text-caption">Privacy</a>&nbsp;&nbsp;<a :href="`https://${domain}/termsofuse.html`" target="_blank" class="text-caption">Terms</a> &copy; 2025 {{appName}} LLC&nbsp;&nbsp;v.{{ appVersion }}&nbsp;&nbsp;</span>
      </div>
      <v-spacer></v-spacer>
    </v-footer>
    <terms-of-service></terms-of-service>
  </v-app>
</template>

<script>
import Bowser from "bowser"
import TermsOfService from '@/components/TermsOfService'
import StoreConstants from '@/StoreConstants'
import EventCache from "@/services/EventCache"
import ReferrerHelper from "@/services/ReferrerHelper";
import SurveyFeedback from "@/components/surveys/SurveyFeedback";
import CouponHelper from "@/services/CouponHelper";
import AndroidInterface from "@/services/AndroidInterface";
import AWSClockSkewHandler from "@/aws/AWSClockSkewHandler";
import E2EEStatus from "@/services/e2ee/E2EEStatus";
import E2EEHelperCache from "@/services/e2ee/E2EEHelperCache";
import E2EEEvictionTracker from "@/services/e2ee/E2EEEvictionTracker";
import SupportApi from "@/aws/support/SupportApi";
import { containsProfanity, PROFANITY_WARNING } from "@/services/ProfanityFilter";
const browser = Bowser.getParser(window.navigator.userAgent);

export default {
  name: 'App',
  components: {
    SurveyFeedback,
    TermsOfService
  },
  data() {
    return {
      domain: process.env.VUE_APP_DOMAIN,
      appName: process.env.VUE_APP_NAME,
      supportDomain: process.env.VUE_APP_SUPPORT_DOMAIN,
      product: process.env.VUE_APP_PRODUCT,
      showFeedbackSurvey: false,
      overrideFeedbackRequest: false,
      loggingOut: false,
      drawer: false,
      displayUnsupportedBrowser: null,
      displayPastDue: null,
      appVersion: process.env.VUE_APP_VERSION,
      clockSkewDetected: false,
      clockSkewOffset: 0,
      e2eeConnectionIssue: false,
      e2eeProbeInFlight: false,
      e2eeLastProbeAt: 0,
      showSupportDialog: false,
      showSupportAlphaWarning: false,
      supportMessage: '',
      showSupportProfanityWarning: false,
      supportProfanityWarning: PROFANITY_WARNING,
      showChatTooltip: false,
      showNewSupport: true,
      hasWaitingTicket: false,
      supportPollTimer: null,
    }
  },
  beforeCreate() {
    console.log(`TIMING: App.vue beforeCreate`)
  },
  mounted () {
    console.log(`TIMING: App.vue mounted`)

    ReferrerHelper.run(this.$route.query.href, this.$route.query.referrer)
    CouponHelper.run(this.$route.query.coupon, this.$route.query.onlyAnnual)

    // News/announcements now live in components/home/LatestNews.vue
    const handlePastDue = async () => {
      await this.$store.state.accountLoadedPromise

      if(!this.$store.getters.isSubscribed && !this.$store.getters.isAccountabilityPartner) {
        return
      }

      this.displayPastDue = this.$store.getters.isAccountPastDue
    }

    handlePastDue()

    window.addEventListener('resize', this.onResize)
    this.onResize()
  },
  beforeUnmount () {
    this.stopSupportPolling()
    if (typeof window !== 'undefined') {
      window.removeEventListener('resize', this.onResize)
    }

    EventCache.cleanUp()
  },
  created () {

    let timeoutHandler
    this.$store.watch((state) => state.inAppCredentialsExpiresAt, (oldValue, newValue) => {
      if(newValue) {
        let value = parseInt(newValue)
        if(timeoutHandler) {
          clearTimeout(timeoutHandler)
        }
        timeoutHandler = setTimeout(() => {
          this.api.forceLogout('credentialsExpired')
        }, Math.max(value - Date.now() - (5 * 60 * 1000), 0))
      }
    })

    if(this.$route.query.inApp) {
      this.$store.commit(StoreConstants.commits.setInApp)
    }

    if(this.$route.query.showAppBar) {
      this.$store.commit(StoreConstants.commits.setShowAppBar, true)
    }

    this.$store.commit(StoreConstants.commits.setBrowser, browser.parsedResult)

    this.displayUnsupportedBrowser = this.isUnsupportedBrowser

    AWSClockSkewHandler.onSignificantSkew = (offset) => {
      this.clockSkewDetected = true
      this.clockSkewOffset = offset
    }

    this.checkServerTimeSkew()

    E2EEStatus.onConnectionIssue = () => {
      this.e2eeConnectionIssue = true
    }
    E2EEStatus.onConnectionRestored = () => {
      this.e2eeConnectionIssue = false
    }
    if(E2EEStatus.hasIssue) {
      this.e2eeConnectionIssue = true
    }

    this.api.handleIdleLogout()

    window.addEventListener('focus', async () => {
      await this.api.handleIdleLogout()
      this.probeE2EEIfBannerShown()
      this.checkServerTimeSkew()
    })

    document.addEventListener('visibilitychange', async () => {
      console.log('visibilitychange fired, hidden=' + document.hidden)
      if (!document.hidden) {
        await this.api.refreshCredentialsIfNeeded()
        await this.api.handleIdleLogout()
        this.probeE2EEIfBannerShown()
        this.checkServerTimeSkew()
      }
    })

    window.addEventListener('beforeunload', () => {
      this.api.handleIdleLogout()
    })
  },
  watch: {
    async '$store.state.loggedInEmail'(newVal) {
      if (newVal) {
        await this.$store.state.accountLoadedPromise
        await this.checkWaitingTickets()
        this.startSupportPolling()
        setTimeout(() => { this.showChatTooltip = true }, 500)
        setTimeout(() => { this.showChatTooltip = false }, 3500)
      } else {
        this.hasWaitingTicket = false
        this.stopSupportPolling()
      }
    }
  },
  methods: {
    async checkServerTimeSkew() {
      // Cache-bust so a backgrounded/bfcache-restored tab never reuses a stale
      // x-server-time captured earlier (which would falsely look like clock skew).
      const url = '/server-time?_=' + Date.now()
      const req = new Request(url, { cache: 'no-store' })
      const clientBefore = Date.now()
      const reqHeaders = {}
      req.headers.forEach((v, k) => { reqHeaders[k] = v })
      console.log('clock skew probe request', JSON.stringify({
        url: req.url,
        method: req.method,
        cache: req.cache,
        clientTimeMs: clientBefore,
        clientTimeIso: new Date(clientBefore).toISOString(),
        tzOffsetMin: new Date().getTimezoneOffset(),
        headers: reqHeaders
      }))
      try {
        const res = await fetch(req)
        const clientAfter = Date.now()
        const resHeaders = {}
        res.headers.forEach((v, k) => { resHeaders[k] = v })
        const rawServerTime = res.headers.get('x-server-time')
        const serverMs = Number(rawServerTime)
        const offset = serverMs ? serverMs - clientAfter : null
        console.log('clock skew probe response', JSON.stringify({
          url: res.url,
          status: res.status,
          ok: res.ok,
          type: res.type,
          clientTimeAfterMs: clientAfter,
          clientTimeAfterIso: new Date(clientAfter).toISOString(),
          roundTripMs: clientAfter - clientBefore,
          rawServerTimeHeader: rawServerTime,
          parsedServerMs: serverMs || null,
          parsedServerIso: serverMs ? new Date(serverMs).toISOString() : null,
          offsetMs: offset,
          offsetMinutes: offset != null ? Math.round(offset / 60000) : null,
          headers: resHeaders
        }))
        if (!serverMs) return
        if (Math.abs(offset) > 5 * 60 * 1000) {
          console.log('clock skew detected, showing banner. offsetMs=' + offset)
          this.clockSkewDetected = true
          this.clockSkewOffset = offset
        } else {
          // Fresh measurement says the clock is fine — clear any stale banner
          // that an earlier (possibly cached) check left open.
          this.clockSkewDetected = false
          this.clockSkewOffset = 0
        }
      } catch (e) {
        console.log('clock skew probe failed', e)
      }
    },
    clearWaitingTicket() {
      this.hasWaitingTicket = false
    },
    async checkWaitingTickets() {
      try {
        const result = await SupportApi.listTickets()
        const tickets = result.data?.tickets || []
        this.hasWaitingTicket = tickets.some(t => t.status === 'waiting_customer')
      } catch (err) {
        console.error('Failed to check waiting tickets:', err)
      }
    },
    startSupportPolling() {
      this.stopSupportPolling()
      this.supportPollTimer = setInterval(() => this.checkWaitingTickets(), 60000)
    },
    stopSupportPolling() {
      if (this.supportPollTimer) {
        clearInterval(this.supportPollTimer)
        this.supportPollTimer = null
      }
    },
    async openSupport() {
      try {
        const result = await SupportApi.listTickets()
        const tickets = result.data?.tickets || []
        const open = tickets.find(t => t.status !== 'closed')
        if (open) {
          this.$router.push(`/support/${encodeURIComponent(open.accountId)}/${encodeURIComponent(open.createdAt)}`)
          return
        }
      } catch (err) {
        console.error('Failed to check tickets:', err)
      }
      this.showSupportDialog = true
    },
    async openE2EESupportTicket() {
      const baseMessage = "My device is having trouble connecting to the end-to-end encryption server. Encrypted screenshots and events aren't loading."
      let diag = ''
      try {
        diag = await this.gatherE2EEDiagnostics()
      } catch(e) {
        console.log('failed to gather e2ee diagnostics', e)
      }
      this.supportMessage = diag
        ? `${baseMessage}\n\n--- Diagnostic info (please keep) ---\n${diag}`
        : baseMessage
      this.showSupportProfanityWarning = false
      this.showSupportDialog = true
    },
    async gatherE2EEDiagnostics() {
      const lines = []

      lines.push(`Last E2EE error: ${E2EEStatus.lastContext || 'none recorded'}`)

      let helperAccountIds = []
      try {
        const helpers = E2EEHelperCache.getAllE2EEHelpers()
        helperAccountIds = helpers.map(h => h.accountId).filter(Boolean)
        if(helperAccountIds.length) {
          lines.push(`E2EE accounts: ${helperAccountIds.join(', ')}`)
        }
      } catch(e) {
        console.log(e)
      }

      try {
        const evictionResults = await Promise.all(
          helperAccountIds.map(async id => {
            const evicted = await E2EEEvictionTracker.isFrequentlyEvicted(id)
            return evicted ? id : null
          })
        )
        const evictedIds = evictionResults.filter(Boolean)
        if(evictedIds.length) {
          lines.push(`Frequently-evicted accounts: ${evictedIds.join(', ')} (browser is clearing site data)`)
        } else if(helperAccountIds.length) {
          lines.push('Frequently-evicted accounts: none')
        }
      } catch(e) {
        console.log(e)
      }

      try {
        const est = await E2EEEvictionTracker.getStorageEstimate()
        if(est) {
          const fmt = b => `${(b / 1024 / 1024).toFixed(1)}MB`
          const pct = (est.usageRatio * 100).toFixed(1)
          const nearlyFull = est.usageRatio > 0.85 ? ' [NEARLY FULL — recommend freeing storage]' : ''
          lines.push(`Device storage: ${fmt(est.usage)} used of ${fmt(est.quota)} quota (${pct}%)${nearlyFull}`)
        } else {
          lines.push('Device storage: unavailable')
        }
      } catch(e) {
        lines.push('Device storage: error reading')
      }

      lines.push(`Online: ${navigator.onLine}`)

      const conn = navigator.connection || navigator.mozConnection || navigator.webkitConnection
      if(conn) {
        const parts = []
        if(conn.effectiveType) parts.push(`type=${conn.effectiveType}`)
        if(typeof conn.downlink === 'number') parts.push(`downlink=${conn.downlink}Mbps`)
        if(typeof conn.rtt === 'number') parts.push(`rtt=${conn.rtt}ms`)
        if(conn.saveData) parts.push('saveData=on')
        lines.push(`Connection: ${parts.join(', ') || 'unknown'}`)
      } else {
        lines.push('Connection: API unavailable (Safari/Firefox)')
      }

      try {
        const parsed = browser.parsedResult || {}
        const os = parsed.os || {}
        const br = parsed.browser || {}
        const platform = parsed.platform || {}
        const osStr = [os.name, os.versionName || os.version].filter(Boolean).join(' ') || 'Unknown'
        const brStr = [br.name, br.version].filter(Boolean).join(' ') || 'Unknown'
        lines.push(`OS: ${osStr}`)
        lines.push(`Browser: ${brStr}`)
        if(platform.type) lines.push(`Platform: ${platform.type}${platform.vendor ? ' (' + platform.vendor + ')' : ''}`)
      } catch(e) {
        console.log(e)
      }

      lines.push(`In native app: ${this.$store.state.inApp ? 'yes' : 'no'}`)
      if(this.appVersion) lines.push(`App version: ${this.appVersion}`)
      const ua = navigator.userAgent || ''
      if(ua) lines.push(`User agent: ${ua}`)

      return lines.join('\n')
    },
    async probeE2EEIfBannerShown() {
      if(!E2EEStatus.hasIssue) return
      if(this.e2eeProbeInFlight) return
      if(this.e2eeLastProbeAt && Date.now() - this.e2eeLastProbeAt < 5000) return
      this.e2eeProbeInFlight = true
      this.e2eeLastProbeAt = Date.now()
      try {
        const helpers = E2EEHelperCache.getAllE2EEHelpers()
        for(const helper of helpers) {
          try {
            await helper.probeConnection()
            return
          } catch(e) {
            console.log('e2ee probe failed for ' + helper.accountId, e)
          }
        }
      } finally {
        this.e2eeProbeInFlight = false
      }
    },
    submitSupportMessage() {
      if (containsProfanity(this.supportMessage)) {
        this.showSupportProfanityWarning = true
        return
      }
      this.showSupportProfanityWarning = false
      this.showSupportAlphaWarning = true
    },
    cancelSupport() {
      this.showSupportDialog = false
      this.showSupportProfanityWarning = false
      this.supportMessage = ''
    },
    submitSupport() {
      const message = this.supportMessage.trim()
      if (!message) return
      this.showSupportDialog = false
      this.showSupportAlphaWarning = false
      this.showSupportProfanityWarning = false
      this.supportMessage = ''
      this.$router.push({ path: '/support/new', query: { message } })
    },
    openFeedbackSurvey() {
      this.showFeedbackSurvey = true
    },
    reloadAccount() {
      this.$store.dispatch(StoreConstants.actions.loadAccount)
    },
    fixPaymentInfo() {
      this.displayPastDue = false
      this.$router.push({
        path: 'account',
        query: {
          changeCard: true
        }
      })
    },
    onResize() {
      let isMobile = window.innerWidth < 600
      this.$store.commit(StoreConstants.commits.setMobile, isMobile)
    },
    isLoggedIn() {
      return !!this.$store.state.loggedInEmail
    },
    openAbout() {
      this.drawer = false
      AndroidInterface.openAbout()
    },
    openBatteryOptimization() {
      this.drawer = false
      AndroidInterface.openBatteryOptimization()
    },
    async logoutAwait(reason, reload) {
      this.loggingOut = true
      await this.auth.logoutAwait(reason)
      setTimeout(async () => {
        this.loggingOut = false
        if(!reload) {
          await this.$router.push('login')
        } else {
          window.location.reload()
        }
      }, 3000)
    }
  },
  computed: {
    homeRoute() {
      return this.$store.getters.isUser ? '/userdevice' : '/home'
    },
    supportBaseDomain() {
      const d = this.supportDomain || ''
      return d.startsWith('support.') ? d.slice(8) : d
    },
    shouldAskForFeedback: {
      get() {
        //disabled by cam on 10/30/2023, setup again once we have it sent to support reps.
        // return false
        return !this.overrideFeedbackRequest && this.$store.getters.shouldAskForFeedback && this.product !== 'screenkin'
      },
      set (val) {
        this.overrideFeedbackRequest = val
      }
    },
    whatsNewContentShown() {
      return this.whatsNewContent.filter(x => {
        return !x.hide
      })
    },
    isUnsupportedBrowser() {
      return this.$store.state.browser.engine.name !== 'Blink' && this.$store.state.browser.browser.name !== 'Chrome' && this.$store.state.browser.browser.name !== 'Firefox' && this.$store.state.browser.browser.name !== 'Safari'
    },
    clockSkewMinutes() {
      return Math.round(Math.abs(this.clockSkewOffset) / 60000)
    }
  }
}
</script>


<style lang="scss">
.text--theme--primary {
  color: $theme-primary !important;
}
.text--theme--accent {
  color: $theme-accent !important;
}
.text--theme--secondary {
  color: $theme-secondary !important;
}

.text--theme--error {
  color: $theme-error !important;
}

.text--theme--warning {
  color: $theme-warning !important;
}

.app-title {
  font-weight: 300 !important;
  font-size: 1.3rem !important;
  letter-spacing: 0.02em;
}

.v-app-bar {
  border-bottom: 3px solid $theme-accent !important;
}

</style>

<style>

  /* Vuetify 3 no longer styles anchor tags with the primary color by default */
  /* v-dialog renders in v-overlay outside .v-application, so target both */
  .v-application a,
  .v-overlay a {
    color: rgb(var(--v-theme-primary)) !important;
  }
  .v-application a:visited,
  .v-overlay a:visited {
    color: rgb(var(--v-theme-primary)) !important;
  }

  #drift-widget-container { z-index: 4 !important;}

  /* Vuetify 2 icon buttons were flat (no background/shadow) by default.
     Vuetify 3 defaults to 'elevated', so restore the old behavior. */
  .v-btn--icon {
    background: transparent !important;
    box-shadow: none !important;
  }

  .v-card__text, .v-card__title {
    word-break: normal; /* maybe !important  see https://github.com/vuetifyjs/vuetify/issues/9130 */
  }

  /* Vuetify 3 card-actions has 8px padding vs 16px for title/text — align them */
  .v-card-actions {
    padding: 0.5rem 1rem;
  }

  .centered {
    display: table;
    text-align: center;
    margin-left: auto;
    margin-right: auto;
  }

  .centered2 {
    margin-left:auto;
    margin-right:auto;
    display: table;
  }

  .clickable {
    cursor: pointer;
  }

  /* SweetAlert2 default z-index (1060) is below Vuetify dialogs (~2400+) */
  .swal2-container {
    z-index: 10000 !important;
  }

  .chat-pulse {
    animation: chat-pulse 1.5s ease-in-out 3;
  }
  @keyframes chat-pulse {
    0%, 100% { transform: scale(1); }
    50% { transform: scale(1.3); }
  }

  .chat-bubble {
    position: absolute;
    right: 48px;
    white-space: nowrap;
    background: #424242;
    color: #fff;
    padding: 6px 12px;
    border-radius: 12px;
    font-size: 13px;
    box-shadow: 0 2px 8px rgba(0,0,0,0.2);
  }

  .chat-bubble-anim-enter-active {
    animation: chat-bubble-in 0.3s ease-out;
  }

  .chat-bubble-anim-leave-active {
    animation: chat-bubble-in 0.2s ease-in reverse;
  }

  @keyframes chat-bubble-in {
    0% { opacity: 0; transform: translateX(8px) scale(0.9); }
    100% { opacity: 1; transform: translateX(0) scale(1); }
  }

  .chat-bubble-tick {
    position: absolute;
    right: -6px;
    top: 50%;
    transform: translateY(-50%);
    width: 0;
    height: 0;
    border-top: 6px solid transparent;
    border-bottom: 6px solid transparent;
    border-left: 8px solid #424242;
  }

</style>
